import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { api, getUser, clearAuth } from "@/lib/api";
import { toast } from "sonner";
import AdSlot from "@/components/AdSlot";
import { Newspaper } from "lucide-react";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const user = getUser();
  const logout = () => { clearAuth(); nav("/"); window.location.reload(); };

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter/subscribe", { email, frequency: "weekly" });
      toast.success("Subscribed! Check your inbox for our weekly digest.");
      setEmail("");
    } catch (err) {
      toast.error("Subscription failed. Try again.");
    } finally { setLoading(false); }
  };

  return (
    <footer className="border-t border-slate-100 bg-white" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 pt-6 pb-8">
        {/* Footer banner ad */}
        <AdSlot placement="footer" className="mb-8"/>
        {/* Newsletter */}
        <div className="rounded-3xl bg-gradient-to-br from-sky-50 via-white to-emerald-50 p-8 md:p-12 border border-slate-100 mb-10" data-testid="newsletter-block">
          <div className="grid md:grid-cols-2 gap-6 items-center">
            <div>
              <div className="overline text-sky-700 mb-2">The EduTime Digest</div>
              <h3 className="font-editorial text-3xl md:text-4xl font-bold text-slate-900 leading-tight">India's education stories, delivered every Monday.</h3>
              <p className="text-slate-600 mt-2">Curated by editors. Powered by AI. Read by Education Community, all over India.</p>
            </div>
            <form onSubmit={subscribe} className="flex flex-col sm:flex-row gap-2" data-testid="newsletter-form">
              <input value={email} onChange={e => setEmail(e.target.value)} type="email" required placeholder="your@email.com"
                className="flex-1 px-5 py-3.5 rounded-full bg-white border border-slate-200 focus:border-sky-400 outline-none text-sm" data-testid="newsletter-email-input" />
              <button disabled={loading} className="px-6 py-3.5 rounded-full bg-sky-700 text-white font-semibold text-sm hover:bg-sky-800 transition-colors disabled:opacity-60" data-testid="newsletter-submit-btn">
                {loading ? "..." : "Subscribe"}
              </button>
            </form>
          </div>
        </div>

        <div className="grid md:grid-cols-4 gap-10">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-xl bg-sky-100 grid place-items-center"><Newspaper className="w-4 h-4 text-sky-700"/></div>
              <span className="font-editorial text-xl font-bold" style={{ color: "#D22B2B" }}>EduTime <span>Today</span></span>
            </div>
            <p className="text-sm text-slate-600 max-w-sm">Premium AI-assisted journalism covering higher education, K-12, EdTech, policy, and research across India. From newsroom to your inbox.</p>
          </div>
          <div>
            <div className="overline text-slate-500 mb-3">Sections</div>
            <ul className="space-y-2 text-sm text-slate-600">
              <li><Link to="/category/university" className="hover:text-sky-700">University</Link></li>
              <li><Link to="/category/school" className="hover:text-sky-700">School</Link></li>
              <li><Link to="/category/edtech" className="hover:text-sky-700">EdTech</Link></li>
              <li><Link to="/category/research" className="hover:text-sky-700">Research</Link></li>
              <li><Link to="/category/results" className="hover:text-sky-700">Results</Link></li>
            </ul>
          </div>
          <div>
            <div className="overline text-slate-500 mb-3">Directory</div>
            <ul className="space-y-2 text-sm text-slate-600">
              <li><Link to="/schools" className="hover:text-sky-700">Schools</Link></li>
              <li><Link to="/colleges" className="hover:text-sky-700">Colleges</Link></li>
              <li><Link to="/suppliers" className="hover:text-sky-700">Suppliers</Link></li>
              <li><Link to="/rankings" className="hover:text-sky-700">Rankings</Link></li>
              <li><Link to="/events" className="hover:text-sky-700">Events</Link></li>
            </ul>
          </div>
          <div>
            <div className="overline text-slate-500 mb-3">Company</div>
            <ul className="space-y-2 text-sm text-slate-600">
              <li><Link to="/about" className="hover:text-sky-700" data-testid="footer-about">About</Link></li>
              <li><Link to="/contact" className="hover:text-sky-700" data-testid="footer-contact">Contact</Link></li>
              <li><Link to="/submit-press-release" className="hover:text-sky-700" data-testid="footer-submit-press">Submit Press Release</Link></li>
              <li><Link to="/contact?type=advertise" className="hover:text-sky-700">Advertise</Link></li>
              {user ? (
                <>
                  {(user.role === "admin" || user.role === "editor") && (
                    <li><Link to="/admin" className="text-sky-700 font-semibold hover:text-sky-800" data-testid="footer-admin">Admin Dashboard</Link></li>
                  )}
                  {(user.role === "institution" || user.role === "supplier") && (
                    <li><Link to="/dashboard" className="text-sky-700 font-semibold hover:text-sky-800" data-testid="footer-dashboard">My Dashboard</Link></li>
                  )}
                  <li><button onClick={logout} className="hover:text-sky-700 text-left" data-testid="footer-logout">Logout</button></li>
                </>
              ) : null}
            </ul>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-slate-100 text-center text-xs text-slate-500" data-testid="footer-copyright">
          © {new Date().getFullYear()} EduTime Today. All rights reserved.
        </div>
      </div>
    </footer>
  );
}
